import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import { FaCamera, FaGlobe, FaDollarSign, FaTrash } from "react-icons/fa";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import defaultProfileImage from "../../../assets/img/profileplace.png";
import { apiV2Service } from "../../../services/apiV2Service";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import { inputValidationService } from "./services/InputValidationService";
import { getCurrencyForCountry } from "../../../utils/countryCurrencyMap";
import { getWebOriginForFlavor, getFlavorForRole } from "../../../utils/deepLink";

export class Profile extends Component {
  constructor() {
    super();
    this.state = {
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNoOptI: "",
      isFirstNameInvalid: false,
      isLastNameInvalid: false,
      isEmailInvalid: false,
      isPhoneNoOpt1Invalid: false,
      hasAvatar: false,
      avatar: "",
      showDeleteModal: false,
      isDeleting: false,
      countryCode: "gh",
      userCountry: null,
      userCurrency: null,
      testCountryId: null,
    };
  }

  componentDidMount = () => {
    // Initialize form with data from props
    this.initializeFormData();
    // Load country and currency info
    this.loadCountryAndCurrency();
    // Check for debug test country
    const debugTestCountry = localStorage.getItem("debug_test_country");
    if (debugTestCountry) {
      this.setState({ testCountryId: parseInt(debugTestCountry) });
    }
  };

  componentDidUpdate = prevProps => {
    // Re-initialize form if currentUser prop changes
    if (prevProps.currentUser !== this.props.currentUser) {
      this.initializeFormData();
      this.loadCountryAndCurrency();
    }
  };

  loadCountryAndCurrency = async () => {
    const { currentUser, countries, currencies } = this.props;
    const { testCountryId } = this.state;

    const countryId = testCountryId || currentUser?.countryId;

    if (!countryId) {
      const kesCurrency = currencies?.find(c => c.code === "KES");
      this.setState({
        userCountry: { name: "Kenya", code: "KE" },
        userCurrency: kesCurrency || { code: "KES", symbol: "KSh" },
      });
      return;
    }

    const userCountry = countries?.find(c => c.id === countryId);

    if (userCountry) {
      // Use country code to get currency code from mapping
      const currencyCode = getCurrencyForCountry(userCountry.code);
      const userCurrency = currencies?.find(c => c.code === currencyCode);

      this.setState({
        userCountry,
        userCurrency: userCurrency || { code: currencyCode, symbol: currencyCode },
      });
    } else {
      const kesCurrency = currencies?.find(c => c.code === "KES");
      this.setState({
        userCountry: null,
        userCurrency: kesCurrency || { code: "KES", symbol: "KSh" },
      });
    }
  };

  handleTestCountryChange = e => {
    const countryId = e.target.value ? parseInt(e.target.value) : null;

    if (countryId) {
      localStorage.setItem("debug_test_country", countryId.toString());
    } else {
      localStorage.removeItem("debug_test_country");
    }

    this.setState({ testCountryId: countryId }, () => {
      this.loadCountryAndCurrency();
      // Trigger currency change event for other components
      window.dispatchEvent(new Event("currencyChanged"));
    });
  };

  initializeFormData = () => {
    const { currentUser } = this.props;

    if (!currentUser) {
      logger.log("No user data provided as props");
      return;
    }

    logger.log("Initializing form with user data:", currentUser);

    // Set basic user info
    this.setState({
      firstName: currentUser.firstName || "",
      lastName: currentUser.lastName || "",
      emailAddress: currentUser.emailAddress || "",
    });

    // Handle phone number from msisdn field
    if (currentUser.msisdn && currentUser.msisdn.trim() !== "") {
      let phoneNumber = currentUser.msisdn.trim();

      // Format phone number
      if (!phoneNumber.startsWith("+")) {
        if (phoneNumber.startsWith("233")) {
          phoneNumber = `+${phoneNumber}`;
          this.setState({ countryCode: "gh" });
        } else if (phoneNumber.startsWith("254")) {
          phoneNumber = `+${phoneNumber}`;
          this.setState({ countryCode: "ke" });
        } else {
          phoneNumber = `+${phoneNumber}`;
        }
      }

      this.setState({
        phoneNoOptI: phoneNumber,
      });
    }

    // Handle avatar/profile image
    if (currentUser.imageUrl && currentUser.imageUrl.trim() !== "") {
      this.setState({
        avatar: currentUser.imageUrl,
        hasAvatar: true,
      });
    }
  };

  componentWillUnmount = () => {
    // Cleanup if needed
  };

  fetchPhoneNumbers = contacts => {
    const phoneNos = [];
    if (contacts && Array.isArray(contacts)) {
      contacts.forEach(contact => {
        if (contact?.type === "mobile" && contact?.value) {
          let phoneNumber = contact.value;

          // If number doesn't start with +, add it
          if (!phoneNumber.startsWith("+")) {
            // Handle country codes
            if (phoneNumber.startsWith("233")) {
              // Ghana
              phoneNumber = `+${phoneNumber}`;
              this.setState({ countryCode: "gh" });
            } else if (phoneNumber.startsWith("254")) {
              // Kenya
              phoneNumber = `+${phoneNumber}`;
              this.setState({ countryCode: "ke" });
            } else {
              phoneNumber = `+${phoneNumber}`;
            }
          }

          logger.log("Formatted phone number:", phoneNumber);
          phoneNos.push(phoneNumber);
        }
      });
    }
    return phoneNos;
  };

  handlePhoneChange = (value, field) => {
    // Validate phone number in real-time
    const isInvalid = value && !isValidPhoneNumber(value);

    this.setState({
      [field]: value || "",
      isPhoneNoOpt1Invalid: isInvalid,
    });
  };

  handleResendEmail = async () => {
    const { currentUser } = this.props;
    const username = currentUser?.username || currentUser?.emailAddress;

    if (!username) return;

    this.setState({ isResending: true });
    try {
      const response = await apiV2Service.resendEmailConfirmation(username);
      showApiMessage(response, "resend verification email");
    } catch (error) {
      showApiMessage(error, "resend verification email");
    } finally {
      this.setState({ isResending: false });
    }
  };

  onSubmit = async () => {
    const { firstName, lastName, emailAddress, phoneNoOptI, avatar } = this.state;
    const { currentUser, onUserUpdate } = this.props;

    if (!currentUser || !currentUser.id) {
      logger.error("No valid user found for update");
      const syntheticError = {
        response: {
          status: 401,
          data: { message: "User session expired. Please login again." },
        },
      };
      showApiMessage(syntheticError, "update profile");
      return;
    }

    // Reset error states and perform validation
    const isFirstNameInvalid = !firstName || firstName.trim() === "";
    const isLastNameInvalid = !lastName || lastName.trim() === "";
    const isEmailInvalid = !emailAddress || !inputValidationService.validateEmail(emailAddress);
    // Phone is optional, but if provided must be valid
    const isPhoneNoOpt1Invalid = phoneNoOptI && !isValidPhoneNumber(phoneNoOptI);

    logger.log("Validating profile form:", {
      isFirstNameInvalid,
      isLastNameInvalid,
      isEmailInvalid,
      isPhoneNoOpt1Invalid,
      phoneNoOptI,
    });

    logger.log("Validating profile form:", {
      isFirstNameInvalid,
      isLastNameInvalid,
      isEmailInvalid,
      isPhoneNoOpt1Invalid,
      emailAddress,
    });

    if (isFirstNameInvalid || isLastNameInvalid || isEmailInvalid || isPhoneNoOpt1Invalid) {
      const msg = `Validation failed: First:${isFirstNameInvalid}, Last:${isLastNameInvalid}, Email:${isEmailInvalid}, Phone:${isPhoneNoOpt1Invalid}`;
      logger.error(msg);
      // window.alert(msg); // Only use if needed, but let's stick to console for now
      this.setState({
        isFirstNameInvalid,
        isLastNameInvalid,
        isEmailInvalid,
        isPhoneNoOpt1Invalid,
      });
      return;
    }

    logger.log("Validation passed! Calling API...");
    // window.alert("Validation passed! Calling API...");

    this.setState({
      isFirstNameInvalid: false,
      isLastNameInvalid: false,
      isEmailInvalid: false,
      isPhoneNoOpt1Invalid: false,
    });

    // Format phone number for API
    const formatPhoneForApi = phone => {
      if (!phone) return "";
      // Remove any non-digit characters except +
      let formatted = phone.replace(/[^\d+]/g, "");
      // Remove + if present
      formatted = formatted.replace(/^\+/, "");
      return formatted;
    };

    this.setState({ isSubmitting: true });

    try {
      // Prepare payload for profile update including email address
      const updatePayload = {
        firstName: firstName || "",
        lastName: lastName || "",
        emailAddress: emailAddress || "",
        msisdn: formatPhoneForApi(phoneNoOptI),
        imageUrl: avatar || "",
      };

      const response = await apiV2Service.updateSelfUser(updatePayload);
      logger.log("Profile update response:", response);
      showApiMessage(response, "update profile");

      // Update the current user in localStorage and parent component
      const updatedUser = response.data?.data || response.data || { ...currentUser, ...updatePayload };
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));
      if (onUserUpdate) onUserUpdate(updatedUser);

      // Dispatch event for other components (like layouts) to refresh
      window.dispatchEvent(new CustomEvent("currentUserUpdated", { detail: updatedUser }));
    } catch (error) {
      logger.error("Error updating profile:", error);
      showApiMessage(error, "update profile");
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleChange = e => {
    const name = e.target.name;
    const value = e.target.value;

    this.setState({
      [name]: value,
    });
  };

  validatePhone = (value, isOptional) => {
    let answer = false;

    if (isOptional) {
      answer = true;
    } else {
      answer = inputValidationService.validatePhoneNo(value);
    }

    return answer;
  };

  currentUserDetails = () => {
    try {
      const jsonStr = localStorage.getItem("currentUser");
      if (!jsonStr) {
        logger.log("No user data in localStorage");
        return null;
      }

      const userDetails = JSON.parse(jsonStr);
      if (!userDetails || !userDetails.id) {
        logger.log("Invalid user data in localStorage:", userDetails);
        return null;
      }

      return userDetails;
    } catch (error) {
      logger.error("Error parsing user data from localStorage:", error);
      return null;
    }
  };

  isAdminUser = () => {
    const { currentUser } = this.props;
    return currentUser?.organizationRole?.code === "ROLE_ADMINISTRATOR";
  };

  triggerImage = _e => {
    this.imageElement.click();
  };

  getBase64 = file =>
    new Promise(function (resolve, reject) {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject("Error: ", error);
    });

  changeImage = async e => {
    const file = e.target.files[0];
    const { onUserUpdate, currentUser } = this.props;

    if (!file) return;

    try {
      // Import the service dynamically to avoid circular dependencies
      const { spaceManagementService } = await import("../../../services/host-apiServices/spaceManagementService");

      // Upload image first to get URL
      const response = await spaceManagementService.uploadMedia([file]);

      if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
        const imageUrl = response.data[0];
        this.setState({ hasAvatar: true, avatar: imageUrl });

        // Immediately update parent component with new image URL
        if (onUserUpdate && currentUser) {
          const updatedUser = { ...currentUser, imageUrl };
          onUserUpdate(updatedUser);
        }
      } else {
        logger.error("Failed to upload image:", response);
      }
    } catch (error) {
      logger.error("Error uploading image:", error);
    }
  };

  handleDeleteImage = async () => {
    const { onUserUpdate, currentUser } = this.props;
    const { firstName, lastName, emailAddress, phoneNoOptI } = this.state;

    if (!currentUser || !currentUser.id) return;

    try {
      const payload = {
        firstName,
        lastName,
        emailAddress,
        msisdn: (phoneNoOptI || "").replace(/[^\d+]/g, "").replace(/^\+/, ""),
        imageUrl: "",
      };

      logger.log("Deleting profile image with V2 API...");
      const response = await apiV2Service.updateSelfUser(payload);
      showApiMessage(response, "delete profile image");

      this.setState({ hasAvatar: false, avatar: "" });

      const updatedUser = { ...currentUser, ...payload };
      localStorage.setItem("currentUser", JSON.stringify(updatedUser));

      // Dispatch events to notify navigation components
      window.dispatchEvent(new CustomEvent("profileUpdated"));
      window.dispatchEvent(new CustomEvent("currentUserUpdated", { detail: updatedUser }));

      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
    } catch (error) {
      logger.error("Error deleting profile image:", error);
      showApiMessage(error, "delete profile image");
    }
  };

  handleDeleteAccount = async () => {
    try {
      this.setState({ isDeleting: true });
      const { currentUser } = this.props;
      const userId = currentUser?.id;

      const response = await apiV2Service.deleteUser(userId);

      if (
        response.status === 200 ||
        response.status === 204 ||
        (response.status === 401 && response.data?.message?.includes("Token has expired"))
      ) {
        // Clear storage and redirect for both successful deletion and token expiration
        localStorage.clear();
        window.location.href = "/signin";
      } else if (response.data?.message?.includes("pending admin approval")) {
        // Show pending approval message
        const syntheticSuccess = {
          status: 200,
          data: {
            message: "Your request to delete your account is pending admin approval.",
          },
        };
        showApiMessage(syntheticSuccess, "delete account");
        this.setState({ showDeleteModal: false });
      } else {
        const syntheticError = {
          response: {
            status: response?.status || 400,
            data: { message: "Failed to delete account. Please try again." },
          },
        };
        showApiMessage(syntheticError, "delete account");
      }
    } catch (error) {
      logger.error("Error deleting account:", error);
      showApiMessage(error, "delete account");
    } finally {
      this.setState({ isDeleting: false });
    }
  };

  render() {
    const {
      firstName,
      lastName,
      emailAddress,
      phoneNoOptI,
      isFirstNameInvalid,
      isLastNameInvalid,
      isEmailInvalid,
      isPhoneNoOpt1Invalid,
      hasAvatar,
      avatar,
      showDeleteModal,
      isDeleting,
      countryCode: _countryCode,
      userCountry,
      userCurrency,
    } = this.state;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Edit Profile</h2>
          <p className="text-sm text-gray-600">Update your personal information and profile picture</p>
        </div>

        {/* Profile Form */}
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="space-y-6">
            {/* Profile Picture Section */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div
                  className="overflow-hidden w-32 h-32 rounded-full border-4 border-gray-100 transition-colors cursor-pointer hover:border-blue-200"
                  onClick={this.triggerImage}
                >
                  <img
                    src={hasAvatar ? avatar : defaultProfileImage}
                    alt="Profile"
                    className="object-cover object-center w-full h-full"
                  />
                </div>
                <div
                  className="absolute right-2 bottom-2 p-2 bg-blue-600 rounded-full transition-colors cursor-pointer hover:bg-blue-700"
                  onClick={this.triggerImage}
                  title="Change profile picture"
                >
                  <FaCamera className="w-4 h-4 text-white" />
                </div>
                {hasAvatar && (
                  <div
                    className="absolute -top-1 -right-1 p-2 bg-red-500 rounded-full transition-colors cursor-pointer hover:bg-red-600 border-2 border-white shadow-sm"
                    onClick={this.handleDeleteImage}
                    title="Delete profile picture"
                  >
                    <FaTrash className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {hasAvatar ? "Profile picture added" : "Click to change profile picture"}
              </p>
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              ref={input => (this.imageElement = input)}
              onChange={e => this.changeImage(e)}
              className="hidden"
              accept="image/*"
            />

            {/* Form Fields */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block mb-1 text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    isFirstNameInvalid ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your first name"
                  defaultValue={firstName}
                  onChange={this.handleChange}
                />
                {isFirstNameInvalid && <p className="mt-1 text-sm text-red-500">First name is required</p>}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block mb-1 text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    isLastNameInvalid ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your last name"
                  defaultValue={lastName}
                  onChange={this.handleChange}
                />
                {isLastNameInvalid && <p className="mt-1 text-sm text-red-500">Last name is required</p>}
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Email Address */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  {this.props.currentUser?.emailConfirmed ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 border border-green-200">
                      Verified
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        Unverified
                      </span>
                      <button
                        type="button"
                        onClick={this.handleResendEmail}
                        disabled={this.state.isResending}
                        className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
                      >
                        {this.state.isResending ? "Resending..." : "Resend Verification"}
                      </button>
                    </div>
                  )}
                </div>
                <input
                  type="email"
                  name="emailAddress"
                  id="emailAddress"
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    isEmailInvalid ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your email address"
                  defaultValue={emailAddress}
                  onChange={this.handleChange}
                />
                {isEmailInvalid && <p className="mt-1 text-sm text-red-500">Valid email is required</p>}
              </div>

              {/* Primary Phone */}
              <div>
                <label htmlFor="phoneNoOptI" className="block mb-1 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="phone-input-wrapper">
                  <PhoneInput
                    international
                    limitMaxLength={true}
                    defaultCountry="KE"
                    value={phoneNoOptI}
                    onChange={value => this.handlePhoneChange(value, "phoneNoOptI")}
                    className={`phone-input ${isPhoneNoOpt1Invalid ? "phone-input-error" : ""}`}
                    inputProps={{ maxLength: 16 }}
                  />
                </div>
                {isPhoneNoOpt1Invalid && <p className="mt-1 text-sm text-red-500">Valid phone number is required</p>}
              </div>
            </div>

            {/* TEST: Country Selector (temporary for testing - localhost only) */}
            {window.location.hostname === "localhost" && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="mb-2 text-sm font-medium text-yellow-800">
                  🧪 TEST MODE: Change country to see currency update
                </p>
                <select
                  value={this.state.testCountryId || ""}
                  onChange={this.handleTestCountryChange}
                  className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
                >
                  <option value="">Select a country...</option>
                  {this.props.countries?.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name} ({country.code}) → {getCurrencyForCountry(country.code)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Country and Currency Display - Hidden for individual customers */}
            {this.props.currentUser?.organization?.type?.code !== "SUBSCRIBER_CUSTOMER" && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Country */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    <FaGlobe className="inline mr-1" />
                    Country
                  </label>
                  <div className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {userCountry?.name || "Kenya"}
                  </div>
                </div>

                {/* Currency */}
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    <FaDollarSign className="inline mr-1" />
                    Currency
                  </label>
                  <div className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-mono">
                    {userCurrency?.code || "KES"} {userCurrency?.symbol && `(${userCurrency.symbol})`}
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="pt-4">
              <button
                type="button"
                onClick={this.onSubmit}
                className="inline-flex items-center px-6 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
              >
                Save Profile
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone section moved to its own page */}

        <Modal
          show={showDeleteModal}
          onHide={() => this.setState({ showDeleteModal: false })}
          centered
          style={{
            borderRadius: "30px",
          }}
        >
          <Modal.Header
            style={{
              border: "none",
              padding: "15px 15px 0 15px",
              display: "flex",
              justifyContent: "flex-end",
              minHeight: "40px",
            }}
          >
            <button
              className="btn-close"
              onClick={() => this.setState({ showDeleteModal: false })}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                color: "#000",
                padding: "0",
                margin: "0",
                opacity: 0.5,
                cursor: "pointer",
                position: "absolute",
                right: "15px",
                top: "10px",
              }}
              aria-label="Close"
            >
              ×
            </button>
          </Modal.Header>
          <Modal.Body
            className="text-center"
            style={{
              padding: "5px 20px 24px 20px",
            }}
          >
            <h5
              className="text-primary"
              style={{
                fontSize: "18px",
                marginTop: "10px",
                marginBottom: "24px",
                fontWeight: "normal",
              }}
            >
              You are about to delete your account
            </h5>
            <div className="gap-3 d-flex justify-content-center">
              <button
                className="btn btn-outline-primary"
                style={{
                  borderRadius: "20px",
                  width: "8rem",
                }}
                onClick={() => this.setState({ showDeleteModal: false })}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-outline-primary"
                style={{
                  borderRadius: "20px",
                  width: "8rem",
                }}
                onClick={this.handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Deleting...</span>
                  </div>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modern Phone Input Styling */}
        <style jsx global>{`
          .phone-input-wrapper .PhoneInput {
            background-color: white !important;
            border: 1px solid #d1d5db !important;
            border-radius: 0.375rem !important;
            padding: 0.5rem 0.75rem !important;
            font-size: 0.875rem !important;
            transition:
              border-color 0.15s ease-in-out,
              box-shadow 0.15s ease-in-out !important;
            display: flex !important;
            align-items: center !important;
          }

          .phone-input-wrapper .PhoneInput:focus-within {
            outline: none !important;
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }

          .phone-input-wrapper .PhoneInput.phone-input-error {
            border-color: #ef4444 !important;
          }

          .phone-input-wrapper .PhoneInput.phone-input-error:focus-within {
            border-color: #ef4444 !important;
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
          }

          .phone-input-wrapper .PhoneInputInput {
            background-color: transparent !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            font-size: 0.875rem !important;
            color: #111827 !important;
            outline: none !important;
            flex: 1 !important;
          }

          .phone-input-wrapper .PhoneInputInput::placeholder {
            color: #9ca3af !important;
          }

          .phone-input-wrapper .PhoneInputCountry {
            margin-right: 0.5rem !important;
            padding: 0 !important;
            border: none !important;
            background: transparent !important;
            display: flex !important;
            align-items: center !important;
            flex-shrink: 0 !important;
          }

          .phone-input-wrapper .PhoneInputCountryIcon {
            margin-right: 0.25rem !important;
            width: 1.25rem !important;
            height: auto !important;
            display: block !important;
          }

          .phone-input-wrapper .PhoneInputCountryIcon img {
            width: 1.25rem !important;
            height: auto !important;
            display: block !important;
          }

          .phone-input-wrapper .PhoneInputCountrySelect {
            color: #111827 !important;
            font-size: 0.875rem !important;
            background: transparent !important;
            border: none !important;
            outline: none !important;
            cursor: pointer !important;
          }

          .phone-input-wrapper .PhoneInputCountrySelectArrow {
            color: #6b7280 !important;
            margin-left: 0.25rem !important;
          }
        `}</style>
      </div>
    );
  }
}

export default Profile;
